import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { SelectData, TaskPanelData } from "../../models";
import { searchProjectOrganisations } from "../../service/query";
import { searchAclEntries } from "../../service/query/QueryService.full";
import { Status } from "../../service/Shared";
import { dataGridMapFilterCriteria } from "../../widget";
import { TaskPanelMode } from "./taskPanelMode";

// Per-record ACL permission that lets an organisation edit a general task (which has no workflow ACL).
const PERMISSION_MANAGE_TASK = "manageTask";

interface UseTaskVisibilityProps {
  mode: TaskPanelMode;
  /*
    The project whose organisations are the audience to choose from, or null when the parent has none — a
    deal or landholding task is visible to the creating org alone, so there is nothing to load.
  */
  visibilityProjectUuid: string | null;
  currentOrganisationUuid: string | null | undefined;
  show: boolean;
  refresh: boolean;
  setCurrentTask: Dispatch<SetStateAction<TaskPanelData>>;
}

interface UseTaskVisibilityReturnData {
  visibilityOrganisationsSelectData: SelectData;
  // The acting org, which is always part of the audience and cannot be deselected.
  defaultSelectedOrgTypeVisibility: string | undefined;
  // Whether the acting org holds manageTask on this record — the edit gate for a general task.
  generalTaskCanEdit: boolean;
  resetVisibility: () => void;
}

/*
  Who a task is visible to, from two sources that answer different questions: the parent project's
  organisations are the options the picker offers, and the task's own ACL is the audience it currently has.
*/
export const useTaskVisibility = ({
  mode,
  visibilityProjectUuid,
  currentOrganisationUuid,
  show,
  refresh,
  setCurrentTask,
}: UseTaskVisibilityProps): UseTaskVisibilityReturnData => {
  const [visibilityOrganisationsSelectData, setVisibilityOrganisationsSelectData] = useState<SelectData>([]);
  const [defaultSelectedOrgTypeVisibility, setDefaultSelectedOrgTypeVisibility] = useState<string>();
  const [generalTaskCanEdit, setGeneralTaskCanEdit] = useState(false);
  // Id of the latest ACL read, so a response for a task the panel has moved on from is discarded.
  const aclRequestIdRef = useRef(0);

  const isCreate = mode.kind === "create";
  const editedTaskUuid = mode.kind === "edit" ? mode.task.uuid : undefined;

  // Options for the Visibility picker. Takes the project explicitly: a general task has no workflow to
  // read it from, so the caller resolves it from the workflow or from the task's own parent.
  const getAndSetDefaultSelectedOrgTypeVisibility = async (projectUuid: string): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.results = {
      role: {
        operator: "in",
        value: "Asset Manager, Developer, Development Manager, Guest - Write",
      },
    };

    const response = await searchProjectOrganisations({
      projectUuid,
      paging: { beforeCursor: null, afterCursor: null, limit: 50 },
      filter: filterCriteria,
    });

    if (response.status === Status.Success && response.data) {
      const selectData: SelectData = response.data.results
        .filter((org) => org.displayName)
        .map((org) => ({ key: org.organisationUuid, value: org.displayName as string }));
      setVisibilityOrganisationsSelectData(selectData);

      const currentOrg = response.data.results.find((f) => f.organisationUuid === currentOrganisationUuid);
      const defaultUuid = currentOrg?.organisationUuid;
      setDefaultSelectedOrgTypeVisibility(defaultUuid);
      // Only a new task takes the default; an existing one keeps the audience its ACL reports.
      if (isCreate && defaultUuid) {
        setCurrentTask((prev) => ({ ...prev, visibility: [defaultUuid] }));
      }
    }
  };

  const getAndSetVisibilityFromAcl = async (): Promise<void> => {
    /*
      Stamped before the no-task check, not after: opening the panel in create mode has to invalidate a
      read still in flight for the task just closed, or that response would apply its organisations as the
      new task's visibility. Only the newest read may write visibility and the manageTask verdict.
    */
    aclRequestIdRef.current += 1;
    const requestId = aclRequestIdRef.current;

    if (editedTaskUuid === undefined) return;

    const response = await searchAclEntries({ resourceUuid: editedTaskUuid });

    if (requestId !== aclRequestIdRef.current) return;

    if (response.status === Status.Success && response.data) {
      const selectedUuids = response.data
        .filter((entry) => entry.permissions.includes(TaskConstants.PERMISSION_READ_WORKFLOW_TASK))
        .map((entry) => entry.subjectUuid);

      if (currentOrganisationUuid && !selectedUuids.includes(currentOrganisationUuid)) {
        selectedUuids.push(currentOrganisationUuid);
      }

      setCurrentTask((prev) => ({ ...prev, visibility: selectedUuids }));

      // General tasks have no workflow ACL — derive edit access from the per-record manageTask grant.
      const myEntry = response.data.find((entry) => entry.subjectUuid === currentOrganisationUuid);
      setGeneralTaskCanEdit(myEntry?.permissions.includes(PERMISSION_MANAGE_TASK) ?? false);
    }
  };

  // Reloads the options when the panel's workflow changes under it, independently of an open/close.
  useEffect(() => {
    if (visibilityProjectUuid) getAndSetDefaultSelectedOrgTypeVisibility(visibilityProjectUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilityProjectUuid, refresh]);

  useEffect(() => {
    if (!show) return;
    // The visibility options need a project, but the per-record ACL does not — a general task's
    // manageTask grant has to be read either way or the panel stays read-only.
    if (visibilityProjectUuid) getAndSetDefaultSelectedOrgTypeVisibility(visibilityProjectUuid);
    getAndSetVisibilityFromAcl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editedTaskUuid, visibilityProjectUuid]);

  const resetVisibility = (): void => setGeneralTaskCanEdit(false);

  return {
    visibilityOrganisationsSelectData,
    defaultSelectedOrgTypeVisibility,
    generalTaskCanEdit,
    resetVisibility,
  };
};
